// app/src/Admin.jsx — admins-only system dashboard (#/admin).
//
// Reads the admins-gated API: GET /admin/health, /admin/metrics/documents,
// /admin/metrics/subscribers. Visible only to members of the `admins` Cognito
// group (the server enforces it too; this just hides the UI from non-admins).

async function adminFetch(path) {
  const cfg = window.kanjo.getConfig();
  const at = await window.kanjo.auth.getAccessToken();
  if (!at) throw new Error('not signed in');
  const res = await fetch(`${cfg.apiBaseUrl}${path}`, { headers: { authorization: `Bearer ${at}` } });
  if (res.status === 403) { const e = new Error('admin only'); e.code = 403; throw e; }
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json();
}

// Minimal dependency-free bar chart.
function BarChart({ series, color = 'var(--accent, #5b8def)', height = 120, everyNthLabel = 1 }) {
  if (!series || !series.length) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--on-dim)', fontSize: 13 }}>No data yet</div>;
  }
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height }}>
        {series.map((s, i) => (
          <div key={i} title={`${s.bucket}: ${s.count}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
            <div style={{ width: '100%', height: `${(s.count / max) * 100}%`, minHeight: s.count > 0 ? 3 : 0, background: color, borderRadius: '3px 3px 0 0' }}/>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
        {series.map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: 'var(--on-dim)', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            {i % everyNthLabel === 0 ? shortLabel(s.bucket) : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

function shortLabel(b) {
  if (!b) return '';
  if (b.includes('T')) return b.slice(11, 13) + 'h';   // hour: "...T14" → "14h"
  if (b.includes('-W')) return b.slice(5);              // week: "2026-W21" → "W21"
  return b.slice(5);                                    // day: "2026-05-21" → "05-21"
}

function Stat({ label, value, sub }) {
  return (
    <div style={{ flex: 1, minWidth: 90, padding: '12px 14px', background: 'var(--surface-2, #f5f5f7)', borderRadius: 12 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink, #111)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-2, #666)', marginTop: 2 }}>{label}</div>
      {sub != null && <div style={{ fontSize: 11, color: 'var(--ink-3, #999)' }}>{sub}</div>}
    </div>
  );
}

function HealthRow({ label, ok, detail }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--hairline, #eee)' }}>
      <span style={{ width: 10, height: 10, borderRadius: 5, background: ok ? 'var(--positive, #2e9e5b)' : 'var(--danger)' }}/>
      <span style={{ flex: 1, fontSize: 14, color: 'var(--on)' }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--on-soft)' }}>{detail}</span>
    </div>
  );
}

// Build the WA monthly filing CSV from the /admin/tax/wa rollup: one row per DOR
// location code, then a totals row and an RTA subtotal. Amounts in dollars.
function waFilingToCsv(r) {
  const d = (c) => ((c || 0) / 100).toFixed(2);
  const esc = (v) => /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
  const lines = [];
  lines.push(['WA Sales Tax Filing', r.month || '']);
  lines.push(['Generated', r.generatedAt || new Date().toISOString()]);
  lines.push([]);
  lines.push(['Location Code', 'Taxable Sales', 'Tax Collected', 'RTA']);
  for (const loc of (r.locations || [])) {
    lines.push([loc.locationCode, d(loc.taxableSalesCents), d(loc.taxCents), loc.rta ? 'Y' : 'N']);
  }
  lines.push([]);
  lines.push(['Total', d(r.totals?.taxableSalesCents), d(r.totals?.taxCents), '']);
  lines.push(['RTA subtotal', d(r.rta?.taxableSalesCents), d(r.rta?.taxCents), '']);
  lines.push(['Charges', r.totals?.count ?? 0]);
  return lines.map((row) => row.map(esc).join(',')).join('\r\n');
}

// WA monthly tax filing → CSV download (admins). Self-contained: picks a month,
// fetches the rollup on demand, and saves a CSV for the DOR return.
function TaxReport() {
  const [month, setMonth] = React.useState(() => localMonth());
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [note, setNote] = React.useState(null);

  const onDownload = async () => {
    if (busy) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      const r = await adminFetch(`/admin/tax/wa?month=${encodeURIComponent(month)}`);
      const csv = waFilingToCsv(r);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `wa-tax-${month}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const n = r.totals?.count || 0;
      setNote(n ? `${n} charge${n === 1 ? '' : 's'} · $${((r.totals?.taxCents || 0) / 100).toFixed(2)} tax` : 'No taxable charges this month.');
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <section>
      <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: '0 0 8px' }}>WA tax filing</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input type="month" value={month} max={localMonth()} onChange={(e) => setMonth(e.target.value)}
          style={{ fontSize: 14, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface,#fff)', color: 'var(--ink,#111)' }} />
        <button onClick={onDownload} disabled={busy}
          style={{ fontSize: 14, fontWeight: 600, padding: '8px 16px', borderRadius: 8, border: 'none', cursor: busy ? 'default' : 'pointer', background: 'var(--accent,#5b8def)', color: '#fff', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>
      {note && <div style={{ fontSize: 12, color: 'var(--on-soft)', marginTop: 6 }}>{note}</div>}
      {err && <ErrorBanner style={{ fontSize: 12, marginTop: 6 }}>{err}</ErrorBanner>}
      <div style={{ fontSize: 11, color: 'var(--on-dim)', marginTop: 6 }}>Taxable sales + tax by DOR location code (RTA bucketed), for the WA monthly return.</div>
    </section>
  );
}

// Demo-mode toggle (admins only). Flipping it persists to localStorage and
// reloads so every screen re-renders with the new scale.
function DemoToggle() {
  const [on, setOn] = React.useState(() => !!(window.kanjo?.demo?.isOn?.()));
  const flip = () => {
    const next = !on;
    if (next && !window.confirm('Turn ON demo mode? All operational money amounts (sales, COGS, cash flow, delivery) will be divided by 1.9 across the app. Subscription pricing is NOT affected.')) return;
    window.kanjo?.demo?.setOn?.(next);
    setOn(next);
    setTimeout(() => window.location.reload(), 120); // refresh every screen
  };
  return (
    <section>
      <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: '0 0 8px' }}>Demo mode</h3>
      <button onClick={flip} role="switch" aria-checked={on}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'var(--surface-2,#f5f5f7)', border: '1px solid var(--hairline,#eee)', borderRadius: 10, padding: '12px 14px', cursor: 'pointer', textAlign: 'left' }}>
        <span>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink,#111)' }}>Scale operational numbers ÷ 1.9</span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-2,#666)', marginTop: 2 }}>{on ? 'On — sales / COGS / cash / delivery shown smaller' : 'Off — real numbers'}</span>
        </span>
        <span aria-hidden="true" style={{ width: 44, height: 26, borderRadius: 13, flexShrink: 0, background: on ? 'var(--brand,#5b8def)' : 'var(--hairline,#ccc)', position: 'relative', transition: 'background .15s' }}>
          <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'left .15s' }}/>
        </span>
      </button>
      <div style={{ fontSize: 11, color: 'var(--on-dim)', marginTop: 6 }}>Subscription pricing isn't affected. Per-device setting.</div>
    </section>
  );
}

// Music-app email registrations (login-less signups via POST /v1/music-register).
// Admins-only list + CSV export. Self-contained, like TaxReport.
function MusicSignups() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    adminFetch('/admin/music-signups')
      .then(setData)
      .catch((e) => setErr(String(e?.message || e)));
  }, []);

  const signups = data?.signups || [];
  const shortDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');

  const onExport = () => {
    // Emails come from a PUBLIC endpoint, so a registrant could submit a value
    // starting with =/+/-/@ (or tab/CR) that a spreadsheet would run as a
    // formula on open. Prefix those with a single quote to neutralize it, then
    // apply normal CSV quoting.
    const esc = (v) => {
      let s = String(v ?? '');
      if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [['Email', 'Source', 'Lang', 'Registered', 'Last seen']]
      .concat(signups.map((s) => [s.email, s.source || '', s.lang || '', s.createdAt || '', s.lastSeenAt || '']))
      .map((r) => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'music-signups.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section>
      <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: '0 0 8px' }}>
        Music registrations{data ? ` · ${data.total}` : ''}
      </h3>
      {err && <ErrorBanner style={{ fontSize: 12, marginBottom: 6 }}>{err}</ErrorBanner>}
      {!data && !err && <div style={{ fontSize: 13, color: 'var(--on-dim)' }}>Loading…</div>}
      {data && signups.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--on-dim)' }}>No registrations yet — they appear here as soon as someone signs up in the app.</div>
      )}
      {signups.length > 0 && (
        <>
          <button onClick={onExport}
            style={{ fontSize: 14, fontWeight: 600, padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--accent,#5b8def)', color: '#fff', marginBottom: 10 }}>
            Download CSV
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
            {signups.map((s) => (
              <div key={s.email} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, padding: '8px 10px', borderRadius: 8, background: 'var(--surface-2,#f5f5f7)' }}>
                <span style={{ fontSize: 13, fontFamily: 'var(--mono)', color: 'var(--ink,#111)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.email}</span>
                <span style={{ fontSize: 11.5, color: 'var(--ink-3,#999)', flexShrink: 0 }}>{shortDate(s.createdAt)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function AdminDashboard() {
  useT();
  const [authz, setAuthz] = React.useState('checking'); // checking | ok | denied
  const [health, setHealth] = React.useState(null);
  const [range, setRange] = React.useState('24h');
  const [docs, setDocs] = React.useState(null);
  const [subs, setSubs] = React.useState(null);
  const [bill, setBill] = React.useState(null);
  const [free, setFree] = React.useState(null);
  const [mboxOpen, setMboxOpen] = React.useState(null);
  const [err, setErr] = React.useState(null);

  // Gate on the admins group (client-side; server enforces too).
  React.useEffect(() => {
    (async () => {
      try {
        const c = await window.kanjo?.auth?.currentClaims?.();
        let g = c && c['cognito:groups'];
        if (typeof g === 'string') g = g.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
        setAuthz(Array.isArray(g) && g.includes('admins') ? 'ok' : 'denied');
      } catch { setAuthz('denied'); }
    })();
  }, []);

  // Load health + subscribers once authorized.
  React.useEffect(() => {
    if (authz !== 'ok') return;
    adminFetch('/admin/health').then(setHealth).catch((e) => setErr(String(e.message || e)));
    adminFetch('/admin/metrics/subscribers').then(setSubs).catch((e) => setErr(String(e.message || e)));
    // Mailbox unread count — fail silently when endpoint not deployed.
    adminFetch('/admin/mailbox?status=open')
      .then((r) => setMboxOpen((r.threads || []).length))
      .catch(() => setMboxOpen(null));
    // Free-tools is optional; on 404/error, render an empty payload so the
    // section shows zeros instead of "Loading…" forever.
    adminFetch('/admin/metrics/freetools')
      .then(setFree)
      .catch(() => setFree({
        signups: { total: 0, byType: {}, byMonth: {}, recent: [] },
        ratings: { total: 0, byCategory: {}, byRating: {}, fiveStar: 0 },
        scans:   { invocations: 0, last30d: [] },
        staff:   { total: 0, counts: { day: 0, week: 0, month: 0 }, byRole: {}, recent: [] },
      }));
    adminFetch('/admin/ledger').then(setBill).catch(() => { /* ledger optional */ });
  }, [authz]);

  // Reload documents when the range changes.
  React.useEffect(() => {
    if (authz !== 'ok') return;
    setDocs(null);
    adminFetch(`/admin/metrics/documents?range=${range}`).then(setDocs).catch((e) => setErr(String(e.message || e)));
  }, [authz, range]);

  if (authz === 'checking') {
    return <KShell tab="today"><KHeader back title={t('header.admin', 'Admin')} /><div style={{ padding: 40, textAlign: 'center', color: 'var(--on-dim)' }}>{t('admin.checking', 'Checking access…')}</div></KShell>;
  }
  if (authz === 'denied') {
    return <KShell tab="today"><KHeader back title={t('header.admin', 'Admin')} /><Empty title={t('admin.adminOnly', 'Admins only')} body={t('admin.adminOnlyBody', 'This dashboard is restricted to system administrators.')} /></KShell>;
  }

  const everyN = range === '24h' ? 3 : range === '30d' ? 5 : 4;

  return (
    <KShell tab="today">
      <KHeader back title={t('reports.adminDash', 'Admin dashboard')} />
      <div style={{ padding: '8px 16px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {err && <ErrorBanner>{err}</ErrorBanner>}

        {/* Inbox tile — links to the admin mailbox with unread count badge. */}
        <a href="#/admin/mailbox"
          style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
          <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4,
                color: 'var(--ink-2,#666)' }}>Inbox</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginTop: 4 }}>
                support@ + hello@getkanjo.com
              </div>
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {mboxOpen != null && (
                <span style={{ background: mboxOpen > 0 ? 'var(--accent,#5b8def)' : 'var(--surface-3,#f0f0f3)',
                  color: mboxOpen > 0 ? '#fff' : 'var(--ink-3)',
                  borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700, minWidth: 22, textAlign: 'center' }}>
                  {mboxOpen} {mboxOpen === 1 ? 'open' : 'open'}
                </span>
              )}
              <Chev size={14}/>
            </div>
          </div>
        </a>

        {/* Acquisition funnel — links to the dedicated funnel dashboard. */}
        <a href="#/admin/funnel"
          style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
          <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4,
                color: 'var(--ink-2,#666)' }}>Acquisition funnel</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginTop: 4 }}>
                {t('admin.funnelSub', 'Landing → signup → bank → activation')}
              </div>
            </div>
            <Chev size={14}/>
          </div>
        </a>


        {/* System health */}
        <section>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: '0 0 8px' }}>
            {t('admin.systemHealth', 'System health')} {health && <span style={{ color: health.ok ? 'var(--positive,#2e9e5b)' : 'var(--danger)' }}>· {health.ok ? 'healthy' : 'attention'}</span>}
          </h3>
          {!health ? <div style={{ color: 'var(--on-dim)', fontSize: 13 }}>Loading…</div> : (
            <div>
              {health.checks?.ocrDlq && <HealthRow label="OCR dead-letter queue" ok={health.checks.ocrDlq.ok} detail={health.checks.ocrDlq.error || `${health.checks.ocrDlq.messages} stuck`} />}
              {health.checks?.lambdaErrors1h && <HealthRow label="Lambda errors (1h)" ok={health.checks.lambdaErrors1h.ok} detail={health.checks.lambdaErrors1h.error || `${health.checks.lambdaErrors1h.count}`} />}
              <div style={{ fontSize: 11, color: 'var(--on-dim)', marginTop: 6 }}>Checked {health.checkedAt && new Date(health.checkedAt).toLocaleTimeString()}</div>
            </div>
          )}
        </section>

        {/* Billing (Finix ledger) */}
        {bill && bill.summary && (
          <section>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: '0 0 8px' }}>Billing</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {[
                ['Revenue', `$${((bill.summary.paymentsCents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
                ['Refunds', `$${((bill.summary.refundsCents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
                ['Net', `$${((bill.summary.netCents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
                ['New subs', String(bill.summary.newSubscribers || 0)],
                ['Cancellations', String(bill.summary.cancellations || 0)],
                ['Failed / disputes', `${bill.summary.failedCount || 0} / ${bill.summary.disputes || 0}`],
              ].map(([label, val]) => (
                <div key={label} style={{ background: 'var(--surface,#fff)', border: '1px solid var(--hairline,#eee)', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, color: 'var(--ink-3,#999)' }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink,#111)' }}>{val}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Music-app email registrations (list + CSV export) */}
        <MusicSignups />

        {/* WA tax filing (CSV download) */}
        <TaxReport />

        {/* Demo mode — scale operational numbers (admins only). */}
        <DemoToggle />

        {/* Documents processed */}
        <section>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: 0 }}>Documents processed</h3>
            <div style={{ display: 'flex', gap: 4 }}>
              {[['24h', '24h'], ['30d', '30d'], ['12mo', '12mo']].map(([k, lbl]) => (
                <button key={k} onClick={() => setRange(k)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', background: range === k ? 'var(--accent,#5b8def)' : 'var(--surface-2,#f0f0f3)', color: range === k ? '#fff' : 'var(--ink-2,#666)' }}>{lbl}</button>
              ))}
            </div>
          </div>
          {!docs ? <div style={{ color: 'var(--on-dim)', fontSize: 13 }}>Loading…</div> : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--on-soft)', marginBottom: 6 }}>
                {docs.series.reduce((a, b) => a + b.count, 0)} total · per {docs.granularity}
              </div>
              <BarChart series={docs.series} everyNthLabel={everyN} />
            </div>
          )}
        </section>

        {/* New subscribers */}
        <section>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: '0 0 8px' }}>New subscribers</h3>
          {!subs ? <div style={{ color: 'var(--on-dim)', fontSize: 13 }}>Loading…</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Stat label="Last 24h" value={subs.counts.day} />
                <Stat label="Week" value={subs.counts.week} />
                <Stat label="Month" value={subs.counts.month} />
                <Stat label="Quarter" value={subs.counts.quarter} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--on-soft)', marginBottom: 6 }}>Per week (52 wk)</div>
                <BarChart series={subs.byWeek} color="var(--positive,#2e9e5b)" everyNthLabel={4} />
              </div>
              {Object.keys(subs.byChannel || {}).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--on-soft)', marginBottom: 4 }}>By channel</div>
                  {Object.entries(subs.byChannel).sort((a, b) => b[1] - a[1]).map(([ch, n]) => (
                    <div key={ch} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid var(--hairline,#eee)' }}>
                      <span style={{ color: 'var(--on)' }}>{ch}</span><span style={{ color: 'var(--on-soft)' }}>{n}</span>
                    </div>
                  ))}
                </div>
              )}
              {subs.note && <div style={{ fontSize: 11, color: 'var(--on-dim)' }}>{subs.note}</div>}
            </div>
          )}
        </section>

        {/* Free tools funnel — getkanjo.com/p rolodex signups, scans, ratings. */}
        <section>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: '0 0 8px' }}>
            Free tools (getkanjo.com/p)
          </h3>
          {!free ? <div style={{ color: 'var(--on-dim)', fontSize: 13 }}>Loading…</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Top-line stats */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Stat label="Signups" value={free.signups?.total ?? 0} />
                <Stat label="Card scans (30d)" value={free.scans?.invocations ?? '—'} sub="Lambda invocations" />
                <Stat label="Ratings" value={free.ratings?.total ?? 0} sub={`${free.ratings?.fiveStar ?? 0} are 5★`} />
                <Stat label="/pro pages claimed" value={free.providerPages?.total ?? 0} sub="service providers" />
              </div>

              {/* Signups by businessType */}
              {free.signups?.byType && Object.keys(free.signups.byType).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--on-soft)', marginBottom: 4 }}>Signups by establishment type</div>
                  {Object.entries(free.signups.byType).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid var(--hairline,#eee)' }}>
                      <span style={{ color: 'var(--on)' }}>{k}</span>
                      <span style={{ color: 'var(--on-soft)' }}>{n}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Recent signups */}
              {free.signups?.recent && free.signups.recent.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--on-soft)', marginBottom: 4 }}>Recent signups</div>
                  <div style={{ fontSize: 12.5, fontFamily: 'var(--mono, ui-monospace)' }}>
                    {free.signups.recent.slice(0, 10).map((r, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--hairline,#eee)', gap: 8 }}>
                        <span style={{ color: 'var(--on-dim)' }}>{(r.createdAt || '').slice(0, 16).replace('T', ' ')}</span>
                        <span style={{ color: 'var(--on)' }}>{r.businessType || '—'}</span>
                        <span style={{ color: 'var(--on-soft)' }}>ZIP {r.zip || '—'}</span>
                        <span style={{ color: 'var(--on-dim)' }}>{r.lang || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ratings by category */}
              {free.ratings?.byCategory && Object.keys(free.ratings.byCategory).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--on-soft)', marginBottom: 4 }}>Ratings by category</div>
                  {Object.entries(free.ratings.byCategory).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid var(--hairline,#eee)' }}>
                      <span style={{ color: 'var(--on)' }}>{k}</span>
                      <span style={{ color: 'var(--on-soft)' }}>{n}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Scans over time (daily, 30d) */}
              {free.scans?.last30d && free.scans.last30d.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--on-soft)', marginBottom: 6 }}>Card scans (daily, 30d)</div>
                  <BarChart series={free.scans.last30d} color="var(--accent,#5b8def)" everyNthLabel={5} />
                </div>
              )}

              {/* Provider pages — claimed /pro profiles */}
              {free.providerPages?.recent && free.providerPages.recent.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--on-soft)', marginBottom: 4 }}>/pro pages claimed</div>
                  <div style={{ fontSize: 12.5, fontFamily: 'var(--mono, ui-monospace)' }}>
                    {free.providerPages.recent.map((r, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--hairline,#eee)', flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--on-dim)' }}>{(r.claimedAt || '').slice(0, 10)}</span>
                        <a href={`https://getkanjo.com/pro/${r.slug}`} target="_blank" rel="noopener" style={{ color: 'var(--accent,#5b8def)', textDecoration: 'none' }}>{r.slug}</a>
                        <span style={{ color: 'var(--on-soft)' }}>{r.phone || '—'}</span>
                        <span style={{ color: 'var(--on)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email || r.website || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Employee sign-ups — getkanjo.com/staff job-seeker Staff Cards. */}
        <section>
          <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: '0 0 8px' }}>
            Employee sign-ups (Staff Cards)
          </h3>
          {!free ? <div style={{ color: 'var(--on-dim)', fontSize: 13 }}>Loading…</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Top-line counts */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Stat label="Total cards" value={free.staff?.total ?? 0} sub="active job-seekers" />
                <Stat label="Last 24h" value={free.staff?.counts?.day ?? 0} />
                <Stat label="Week" value={free.staff?.counts?.week ?? 0} />
                <Stat label="Month" value={free.staff?.counts?.month ?? 0} />
              </div>

              {/* By role */}
              {free.staff?.byRole && Object.keys(free.staff.byRole).length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--on-soft)', marginBottom: 4 }}>By role</div>
                  {Object.entries(free.staff.byRole).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: '1px solid var(--hairline,#eee)' }}>
                      <span style={{ color: 'var(--on)' }}>{k}</span>
                      <span style={{ color: 'var(--on-soft)' }}>{n}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Recent sign-ups */}
              {free.staff?.recent && free.staff.recent.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--on-soft)', marginBottom: 4 }}>Recent sign-ups</div>
                  <div style={{ fontSize: 12.5, fontFamily: 'var(--mono, ui-monospace)' }}>
                    {free.staff.recent.map((r, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--hairline,#eee)', gap: 8 }}>
                        <span style={{ color: 'var(--on-dim)' }}>{(r.createdAt || '').slice(0, 16).replace('T', ' ')}</span>
                        <span style={{ color: 'var(--on)' }}>{r.role || '—'}</span>
                        <span style={{ color: 'var(--on-dim)' }}>{r.lang || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {free.staff?.error && <div style={{ fontSize: 11, color: 'var(--on-dim)' }}>{free.staff.error}</div>}
            </div>
          )}
        </section>

        {/* Temporary: import bank transactions from a CSV until Plaid prod access. */}
        <CsvImport />
      </div>
    </KShell>
  );
}

// ─── CSV bank-statement import (TEMPORARY, admins only) ──────────────────────
// Loads checking-account transactions from a CSV (Chase business export shape:
// Details, Posting Date, Description, Amount, Type, Balance) into Dexie's
// plaidTransactions, so they behave exactly like Plaid-synced data until Plaid
// production access lands (~40 days). Idempotent: a deterministic plaidTxId per
// row means re-importing the same YTD file overwrites instead of duplicating.
// REMOVE this whole block (+ the <CsvImport/> mount + the 'csv-import' synthetic
// item/account) once Plaid is live.

// RFC4180-ish parser supporting quoted fields; delimiter is auto-detected.
function parseDelimited(text, delim) {
  const rows = []; let row = []; let field = ''; let i = 0; let inQ = false;
  const endField = () => { row.push(field); field = ''; };
  const endRow = () => { rows.push(row); row = []; };
  while (i < text.length) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i += 2; continue; } inQ = false; i++; continue; }
      field += c; i++; continue;
    }
    if (c === '"') { inQ = true; i++; continue; }
    if (c === delim) { endField(); i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { endField(); endRow(); i++; continue; }
    field += c; i++;
  }
  if (field.length || row.length) { endField(); endRow(); }
  return rows.filter((r) => r.some((x) => x !== ''));
}

// 53-bit string hash → stable, collision-resistant id for dedupe.
function cyrb53(str, seed = 0) {
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

function csvToIso(s) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(String(s || '').trim());
  if (!m) { const d = new Date(s); return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10); }
  return `${m[3]}-${String(+m[1]).padStart(2, '0')}-${String(+m[2]).padStart(2, '0')}`;
}

// Pull a readable merchant from a verbose ACH/bill-pay description.
function merchantFromDesc(desc) {
  const d = String(desc || '').replace(/\s+/g, ' ').trim();
  let m = /ORIG CO NAME:\s*(.+?)\s+ORIG ID/i.exec(d);
  if (m) return m[1].trim();
  m = /\bTo\s+(.+?)\s+\d{1,2}\/\d{1,2}\b/i.exec(d);          // "Online Payment To X 05/22"
  if (m) return m[1].trim();
  m = /Zelle payment to\s+(.+)/i.exec(d);
  if (m) return m[1].trim();
  m = /^CHECK\s+(\S+)/i.exec(d);
  if (m) return `Check #${m[1]}`;
  return d.slice(0, 48);
}

function csvRowToTx(cols, idx) {
  const iso = csvToIso(cols[idx.date]);
  const amt = parseFloat(String(cols[idx.amount] || '').replace(/[$,]/g, ''));
  if (!iso || !Number.isFinite(amt)) return null;
  const desc = String(cols[idx.description] || '').replace(/\s+/g, ' ').trim();
  // Chase: negative = debit (outflow). Plaid convention: positive = outflow → flip.
  const amountCents = Math.round(-amt * 100);
  const bal = idx.balance != null ? parseFloat(String(cols[idx.balance] || '').replace(/[$,]/g, '')) : NaN;
  return {
    // Key on the FULL description so distinct rows that share a date + amount
    // (CHECK 353 vs CHECK 354, two transfers with different transaction#s) stay
    // separate. The "Remove duplicate transactions" cleanup handles the
    // confirmation-number near-duplicate case with a subset rule.
    plaidTxId: `csv-${cyrb53(`${iso}|${amountCents}|${desc}`)}`,
    date: iso, amountCents, merchantName: merchantFromDesc(desc),
    description: desc, type: String(cols[idx.type] || '').trim(),
    balance: Number.isFinite(bal) ? bal : null,
  };
}

function parseStatement(text) {
  const headerLine = (text.split(/\r?\n/, 1)[0]) || '';
  const delim = headerLine.split('\t').length > headerLine.split(',').length ? '\t' : ',';
  const rows = parseDelimited(text, delim);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.toLowerCase().trim());
  const find = (names) => { for (const n of names) { const i = header.indexOf(n); if (i >= 0) return i; } return null; };
  const idx = {
    date: find(['posting date', 'date', 'transaction date']),
    description: find(['description', 'desc', 'memo']),
    amount: find(['amount', 'amt']),
    type: find(['type']),
    balance: find(['balance']),
  };
  if (idx.date == null || idx.amount == null || idx.description == null) {
    throw new Error('CSV needs Date, Description and Amount columns');
  }
  const seen = new Set(); const out = [];
  for (let r = 1; r < rows.length; r++) {
    const tx = csvRowToTx(rows[r], idx);
    if (!tx || seen.has(tx.plaidTxId)) continue;     // drop in-file exact dupes
    seen.add(tx.plaidTxId); out.push(tx);
  }
  return out;
}

const CSV_ITEM_ID = 'csv-import';
const CSV_ACCT_ID = 'csv-checking';

async function importCsvTxs(txs, userId) {
  const db = window.kanjo.db;
  const records = txs.map((t) => ({
    plaidTxId: t.plaidTxId, userId, plaidItemId: CSV_ITEM_ID, plaidAccountId: CSV_ACCT_ID,
    date: t.date, amountCents: t.amountCents, pending: false,
    merchantName: t.merchantName, description: t.description, category: t.type, source: 'csv',
  }));
  await db.plaidTransactions.bulkPut(records);
  // Synthetic item + account so the cash-position screen treats this like a bank.
  await db.plaidItems.put({ plaidItemId: CSV_ITEM_ID, userId, status: 'HEALTHY', institutionName: 'Imported (CSV)', lastSyncedAt: Date.now() });
  const existingAcct = (await db.plaidAccounts.get(CSV_ACCT_ID)) || {};
  const acct = { ...existingAcct, plaidAccountId: CSV_ACCT_ID, userId, plaidItemId: CSV_ITEM_ID, type: 'depository', subtype: 'checking', name: 'Checking (CSV import)', currency: 'USD' };
  // Set the current balance from the running balance of the MOST RECENT
  // transaction (the newest end of the file), so the cash position matches the
  // bank. Direction-aware (files export oldest- or newest-first) so same-day
  // rows resolve to the day's FINAL balance, and guarded so re-importing an
  // OLDER statement can't regress a newer balance. (acct spreads existingAcct,
  // so leaving it untouched preserves the prior balance.)
  if (txs.some((t) => t.balance != null)) {
    const ascending = txs[0].date <= txs[txs.length - 1].date;
    let pick = null;
    if (ascending) { for (let i = txs.length - 1; i >= 0; i--) if (txs[i].balance != null) { pick = txs[i]; break; } }
    else { for (let i = 0; i < txs.length; i++) if (txs[i].balance != null) { pick = txs[i]; break; } }
    if (pick && (existingAcct.balanceAsOf == null || pick.date >= existingAcct.balanceAsOf)) {
      acct.currentBalanceCents = Math.round(pick.balance * 100);
      acct.balanceAsOf = pick.date;
    }
  }
  await db.plaidAccounts.put(acct);
}

// Remove transactions that are the SAME payment imported twice — e.g. a bill-pay
// exported once with its confirmation number ("Online Payment 28944566791 To
// Panos Properties 05/22") and once without ("Online Payment To Panos Properties
// 05/22"). The test is strict to avoid deleting genuinely distinct rows that
// merely share a date + amount: two rows collapse only when their digit-stripped
// descriptions are identical AND one row's set of numbers is a SUBSET of the
// other's. So CHECK 353 vs CHECK 354, or two transfers with different
// transaction#s, are KEPT (their number sets differ). Returns the count removed.
//
// Scope: ONLY manually-imported rows (source 'csv' / 'upload'). The live Plaid
// bank feed is deduped upstream by Plaid's transaction_id and never carries this
// confirmation-number double, so it's left untouched — deleting a feed row here
// would strand it (it won't return until a full resync).
async function removeDuplicateTxs(userId) {
  const db = window.kanjo.db;
  const all = await db.plaidTransactions.where('userId').equals(userId).toArray();
  const txs = all.filter((t) => t.source === 'csv' || t.source === 'upload');
  const text = (t) => String(t.description || t.merchantName || '');
  const descNoDigits = (s) => s.toLowerCase().replace(/\d+/g, ' ').replace(/\s+/g, ' ').trim();
  const numbersOf = (s) => (s.match(/\d+/g) || []);
  const isSubset = (small, big) => { // is multiset `small` ⊆ `big`?
    const c = new Map();
    for (const n of big) c.set(n, (c.get(n) || 0) + 1);
    for (const n of small) { const k = c.get(n) || 0; if (!k) return false; c.set(n, k - 1); }
    return true;
  };
  const groups = new Map();
  for (const t of txs) {
    const k = `${t.date}|${t.amountCents}|${descNoDigits(text(t))}`;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(t);
  }
  const toDelete = [];
  for (const g of groups.values()) {
    if (g.length < 2) continue;
    // Richest (most numbers, then longest text) first; drop a row only when its
    // number-set is a subset of an already-kept row's.
    const rows = g.map((t) => ({ t, nums: numbersOf(text(t)) }))
      .sort((a, b) => b.nums.length - a.nums.length
        || text(b.t).length - text(a.t).length
        || String(a.t.plaidTxId).localeCompare(String(b.t.plaidTxId)));
    const kept = [];
    for (const r of rows) {
      // Drop a row only when a KEPT row carries STRICTLY MORE numbers and r's set
      // is a subset of it — i.e. the survivor is the same payment plus an extra
      // confirmation number. Equal-size number sets are left alone (two genuine
      // same-date/amount payments, e.g. two un-numbered counter deposits, must
      // not collapse into one).
      if (kept.some((k) => k.nums.length > r.nums.length && isSubset(r.nums, k.nums))) toDelete.push(r.t.plaidTxId);
      else kept.push(r);
    }
  }
  if (toDelete.length) await db.plaidTransactions.bulkDelete(toDelete);
  return toDelete.length;
}

function CsvImport() {
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [dedup, setDedup] = React.useState(null);

  const doDedup = async () => {
    setDedup({ busy: true });
    try {
      const claims = await window.kanjo.auth.currentClaims();
      const n = await removeDuplicateTxs(claims.sub);
      if (n > 0) {
        try { await window.kanjo.revenue.sales.syncFromDeposits(); } catch (e) { /* non-fatal */ }
        try { window.kanjo.backup.markBooksDirty(); } catch (e) { /* non-fatal */ }
      }
      setDedup({ busy: false, removed: n });
    } catch (e) { setDedup({ busy: false, error: String(e.message || e) }); }
  };
  const fmt = (c) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((c || 0) / 100);

  const onFile = async (e) => {
    setErr(null); setResult(null); setPreview(null);
    const f = e.target.files && e.target.files[0]; e.target.value = '';
    if (!f) return;
    try {
      const txs = parseStatement(await f.text());
      if (!txs.length) { setErr('No transactions found — check the file.'); return; }
      const existing = await window.kanjo.db.plaidTransactions.bulkGet(txs.map((t) => t.plaidTxId));
      const dup = existing.filter(Boolean).length;
      const dates = txs.map((t) => t.date).sort();
      const withBal = txs.filter((t) => t.balance != null).sort((a, b) => (a.date < b.date ? 1 : -1));
      setPreview({ txs, dup, fresh: txs.length - dup, from: dates[0], to: dates[dates.length - 1], balCents: withBal.length ? Math.round(withBal[0].balance * 100) : null });
    } catch (e2) { setErr(String(e2.message || e2)); }
  };

  const doImport = async () => {
    if (!preview || busy) return;
    setBusy(true); setErr(null);
    try {
      const claims = await window.kanjo.auth.currentClaims();
      await importCsvTxs(preview.txs, claims.sub);
      // Deposits = revenue: turn the imported deposits into daily sales right away.
      try { await window.kanjo.revenue.sales.syncFromDeposits(); }
      catch (e) { console.warn('[csv] sales sync failed', String(e && e.message || e)); }
      // Dehydrate the books to the cloud blob (survives sign-out, reaches viewers
      // + other devices). Raw Dexie writes above bypass the mutate() instrument,
      // so trigger the debounced owner snapshot explicitly.
      try { window.kanjo.backup.markBooksDirty(); } catch (e) { /* non-fatal */ }
      setResult({ added: preview.fresh, updated: preview.dup, total: preview.txs.length });
      setPreview(null);
    } catch (e2) { setErr('Import failed: ' + String(e2.message || e2)); }
    finally { setBusy(false); }
  };

  return (
    <section>
      <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--on-soft)', margin: '0 0 8px' }}>
        Import bank CSV <span style={{ color: 'var(--on-dim)', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>· temporary, until Plaid</span>
      </h3>
      <p style={{ fontSize: 12.5, color: 'var(--on-dim)', margin: '0 0 10px', lineHeight: 1.5 }}>
        Load checking transactions from a CSV export (Chase shape: Details, Posting Date, Description, Amount, Type, Balance). Safe to re-run — duplicates are skipped.
      </p>
      <label style={{ display: 'inline-block', padding: '9px 14px', borderRadius: 10, background: 'var(--surface-2,#f0f0f3)', color: 'var(--ink,#111)', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--hairline,#ddd)' }}>
        Choose CSV…
        <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} style={{ display: 'none' }} />
      </label>

      {err && <ErrorBanner style={{ marginTop: 10 }}>{err}</ErrorBanner>}

      {preview && (
        <div style={{ marginTop: 12, padding: 14, borderRadius: 12, background: 'var(--surface-2,#f5f5f7)', border: '1px solid var(--hairline,#ddd)' }}>
          <div style={{ fontSize: 14, color: 'var(--ink,#111)' }}>
            <b>{preview.txs.length}</b> transactions · <b>{preview.fresh}</b> new · {preview.dup} already imported
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2,#666)', marginTop: 4 }}>
            {preview.from} → {preview.to}{preview.balCents != null ? ` · latest balance ${fmt(preview.balCents)}` : ''}
          </div>
          <button onClick={doImport} disabled={busy}
            style={{ marginTop: 12, padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--accent,#5b8def)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer' }}>
            {busy ? 'Importing…' : `Import ${preview.fresh} new`}
          </button>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--positive,#2e9e5b)' }}>
          Imported {result.total} transactions ({result.added} added, {result.updated} updated). Cashflow now reflects them.
        </div>
      )}

      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--hairline,#ddd)' }}>
        <button onClick={doDedup} disabled={dedup?.busy}
          style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid var(--hairline,#ddd)', background: 'var(--surface-2,#f0f0f3)', color: 'var(--ink,#111)', fontSize: 13, fontWeight: 600, cursor: dedup?.busy ? 'default' : 'pointer' }}>
          {dedup?.busy ? 'Removing…' : 'Remove duplicate transactions'}
        </button>
        {dedup && !dedup.busy && dedup.removed != null && (
          <span style={{ marginLeft: 10, fontSize: 13, color: dedup.removed > 0 ? 'var(--positive,#2e9e5b)' : 'var(--on-dim)' }}>
            {dedup.removed > 0 ? `Removed ${dedup.removed} duplicate${dedup.removed === 1 ? '' : 's'}.` : 'No duplicates found.'}
          </span>
        )}
        {dedup?.error && <span style={{ marginLeft: 10, fontSize: 13, color: 'var(--danger)' }}>{dedup.error}</span>}
        <div style={{ fontSize: 11.5, color: 'var(--on-dim)', marginTop: 6, lineHeight: 1.5 }}>
          Collapses transactions that share a date, amount, and payee but were imported twice (e.g. with and without a confirmation number). Keeps one.
        </div>
      </div>
    </section>
  );
}

// ─── Acquisition funnel dashboard (#/admin/funnel) ───────────────────────────
// Reads GET /admin/metrics/funnel — distinct visitors (anon id) + signed-in
// users (Cognito sub) per stage, landing → signup → bank → activation.

const FUNNEL_LABELS = {
  landing_view:          'Landing views',
  signup_start:          'Signup started',
  signup_complete:       'Signup completed',
  plaid_connect_success: 'Bank connected',
  activation:            'Activated (first scan)',
};

// One horizontal funnel bar: width scaled to the top stage; shows the distinct
// visitor count, the % of the top stage, and step conversion from the stage above.
function FunnelBar({ stage, top, prev, isFirst }) {
  const pctTop = top ? Math.round((stage.visitors / top) * 100) : 0;
  const step = isFirst ? null : (prev ? Math.round((stage.visitors / prev) * 100) : 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 14, color: 'var(--ink,#111)', fontWeight: 600 }}>
          {FUNNEL_LABELS[stage.event] || stage.event}
        </span>
        <span style={{ fontSize: 13, color: 'var(--on-soft)' }}>
          <b style={{ color: 'var(--ink,#111)' }}>{stage.visitors}</b> · {pctTop}%
          {step != null && <span style={{ color: 'var(--on-dim)' }}> · step {step}%</span>}
        </span>
      </div>
      <div style={{ height: 14, borderRadius: 7, background: 'var(--surface-2,#f0f0f3)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(pctTop, stage.visitors > 0 ? 2 : 0)}%`, height: '100%',
          background: 'var(--accent,#5b8def)', borderRadius: 7, transition: 'width .2s' }} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--on-dim)' }}>
        {stage.signedIn} signed-in · {stage.events} event{stage.events === 1 ? '' : 's'}
      </div>
    </div>
  );
}

function FunnelDashboard() {
  useT();
  const [authz, setAuthz] = React.useState('checking');
  const [range, setRange] = React.useState('30d');
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const c = await window.kanjo?.auth?.currentClaims?.();
        let g = c && c['cognito:groups'];
        if (typeof g === 'string') g = g.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
        setAuthz(Array.isArray(g) && g.includes('admins') ? 'ok' : 'denied');
      } catch { setAuthz('denied'); }
    })();
  }, []);

  React.useEffect(() => {
    if (authz !== 'ok') return;
    setData(null); setErr(null);
    adminFetch(`/admin/metrics/funnel?range=${range}`).then(setData).catch((e) => setErr(String(e.message || e)));
  }, [authz, range]);

  if (authz === 'checking') {
    return <KShell tab="today"><KHeader back title={t('admin.funnel', 'Funnel')} /><div style={{ padding: 40, textAlign: 'center', color: 'var(--on-dim)' }}>{t('admin.checking', 'Checking access…')}</div></KShell>;
  }
  if (authz === 'denied') {
    return <KShell tab="today"><KHeader back title={t('admin.funnel', 'Funnel')} /><Empty title={t('admin.adminOnly', 'Admins only')} body={t('admin.adminOnlyBody', 'This dashboard is restricted to system administrators.')} /></KShell>;
  }

  const stages = data?.stages || [];
  const top = stages[0]?.visitors || 0;
  const overall = top ? Math.round(((stages[stages.length - 1]?.visitors || 0) / top) * 100) : 0;

  return (
    <KShell tab="today">
      <KHeader back title={t('admin.funnelTitle', 'Acquisition funnel')} />
      <div style={{ padding: '8px 16px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {err && <ErrorBanner>{err}</ErrorBanner>}

        {/* Range toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--on-soft)' }}>
            {t('admin.funnelBlurb', 'Landing → signup → bank → activation, by distinct visitor.')}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            {[['7d', '7d'], ['30d', '30d'], ['90d', '90d'], ['all', 'All']].map(([k, lbl]) => (
              <button key={k} onClick={() => setRange(k)} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', background: range === k ? 'var(--accent,#5b8def)' : 'var(--surface-2,#f0f0f3)', color: range === k ? '#fff' : 'var(--ink-2,#666)' }}>{lbl}</button>
            ))}
          </div>
        </div>

        {!data ? (
          <div style={{ color: 'var(--on-dim)', fontSize: 13 }}>{t('admin.loading', 'Loading…')}</div>
        ) : top === 0 ? (
          <Empty title={t('admin.funnelEmpty', 'No funnel data yet')} body={t('admin.funnelEmptyBody', 'Events appear here as visitors land on getkanjo.com and move through signup.')} />
        ) : (
          <>
            {/* Top-line: entry, activation, overall conversion */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Stat label="Landing views" value={top} />
              <Stat label="Activated" value={stages[stages.length - 1]?.visitors ?? 0} />
              <Stat label="Overall" value={`${overall}%`} sub="landing → activation" />
            </div>

            {/* Funnel bars */}
            <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {stages.map((s, i) => (
                <FunnelBar key={s.event} stage={s} top={top} prev={i > 0 ? stages[i - 1].visitors : null} isFirst={i === 0} />
              ))}
            </section>

            <div style={{ fontSize: 11, color: 'var(--on-dim)' }}>
              {t('admin.funnelFoot', 'Visitors = distinct anon ids · signed-in = distinct accounts · step = conversion from the stage above.')}
              {data.since && ` · since ${String(data.since).slice(0, 10)}`}
            </div>
          </>
        )}
      </div>
    </KShell>
  );
}

Object.assign(window, { AdminDashboard, FunnelDashboard });
